
import React from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { PerformanceBadge } from '@/components/brand/PerformanceBadge'
import { PerformanceRating } from '@/lib/types'
import { useBrandAware } from '@/hooks/useBrandAware'

interface MetricCardProps {
  title: string
  value: number | string
  loading?: boolean
  error?: boolean
  showPerformance?: boolean
  performanceValue?: number
  icon?: React.ReactNode
  trend?: 'up' | 'down' | 'neutral'
  className?: string
  onClick?: () => void
}

const getPerformanceRating = (value: number): PerformanceRating => {
  if (value >= 8) return 'great'
  if (value >= 5) return 'good'
  if (value >= 2) return 'okay'
  return 'bad'
}

export function MetricCard({
  title,
  value,
  loading = false,
  error = false,
  showPerformance = false,
  performanceValue = 0,
  icon,
  trend = 'neutral',
  className = '',
  onClick
}: MetricCardProps) {
  const { getBrandProps } = useBrandAware()
  const brandCardProps = getBrandProps('cards')
  // Use brand variant if enabled, otherwise default
  const cardVariant = brandCardProps.variant ? 'brand' : 'default'
  if (loading) {
    return (
      <Card variant={cardVariant} className={`h-32 ${className}`}>
        <CardContent className="p-6 h-full flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <Skeleton className="h-4 w-24" />
            {icon && <div className="opacity-50">{icon}</div>}
          </div>
          <Skeleton className="h-9 w-16 mb-2" />
          {showPerformance && <Skeleton className="h-6 w-12" />}
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card variant={cardVariant} className={`border border-ascot shadow-sm h-32 ${className}`}>
        <CardContent className="p-6 h-full flex flex-col justify-between">
          <h3 className="font-sora font-medium text-sm text-midnight/70 mb-2">
            {title}
          </h3>
          <div className="text-3xl font-bold text-ascot mb-2">--</div>
          <span className="text-xs text-ascot">Error loading</span>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card 
      variant={cardVariant}
      className={`hover:shadow-md transition-shadow h-32 ${onClick ? 'cursor-pointer' : ''} ${className}`}
      onClick={onClick}
    >
      <CardContent className="p-6 h-full flex flex-col justify-center items-center text-center">
        <div className="flex items-center justify-center mb-2 w-full">
          <h3 className="font-sora font-medium text-sm text-midnight/70 text-center">
            {title}
          </h3>
          {icon && <div className="text-midnight/50 ml-2">{icon}</div>}
        </div>
        
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="text-3xl font-bold text-midnight text-center">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </div>
          
          {trend !== 'neutral' && (
            <div className={`text-xs px-2 py-1 rounded-full ${
              trend === 'up' 
                ? 'bg-pine/10 text-pine' 
                : 'bg-ascot/10 text-ascot'
            }`}>
              {trend === 'up' ? '↗' : '↘'}
            </div>
          )}
        </div>
        
        {showPerformance && (
          <div className="flex justify-center">
            <PerformanceBadge 
              rating={getPerformanceRating(performanceValue)}
              value={`${performanceValue}%`}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
